/**
 * Account AI keys section (OCP-497) - lives on the "orboto AI" page.
 *
 * Self-service management of account-scoped orboto AI API keys: mint (reveal-
 * once), list (prefix + status + last-used, never material), revoke. Plus a
 * short "use orboto AI via API" snippet (base URL + OpenAI-compatible
 * /v1/embeddings + model) so a customer can wire it into their own scripts /
 * integrations / self-hosted orboto instance. Mirrors the Stripe/Resend
 * reveal-once UX (same as MailApiKeysPage): the plaintext key is held only in
 * React state until the modal closes.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api } from '../lib/api';
import { useTransientState } from '../lib/useTransientState';
import { RoleReadOnlyNotice } from './RoleReadOnlyNotice';
import { usePortalAccess } from '../lib/use-portal-access';
import { fmtDate } from '../lib/format-time';

interface KeySummary {
  id: string;
  name: string | null;
  key_prefix: string;
  created_via: 'portal' | 'operator';
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  /** OCP-770 - final accounting of a revoked key (null while live). */
  final_accounting?: 'pending' | 'complete' | null;
  blocked_at: string | null;
  status: 'active' | 'blocked' | 'revoked';
  gated: boolean;
}

interface ApiInfo {
  base_url: string;
  embeddings_path: string;
  model: string;
  key_cap: number;
}

interface ListResponse {
  gateway_configured: boolean;
  /**
   * Optional on purpose: during a rolling deploy the portal bundle can be
   * newer than the API it talks to, and an older payload has no `api` block.
   * A missing block must degrade the snippet, never crash the whole AI page.
   */
  api?: ApiInfo;
  api_keys: KeySummary[];
}

interface Minted {
  id: string;
  name: string | null;
  key: string;
  key_prefix: string;
  created_at: string;
}

/** Mirrors the server-side `ai.account_key_cap` default - only used when the payload omits it. */
const DEFAULT_KEY_CAP = 10;

type Modal =
  | { kind: 'create' }
  | { kind: 'reveal'; minted: Minted }
  | { kind: 'confirm-revoke'; key: KeySummary }
  | null;

export function AccountAiKeysSection() {
  const { t } = useTranslation('api-keys');
  const { canManageInfra } = usePortalAccess();
  const [data, setData] = useState<ListResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [modal, setModal] = useState<Modal>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const r = await api<ListResponse>('/v1/portal/ai/api-keys', { signal });
      setData(r);
      setState('idle');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setErrorDetail(err instanceof ApiError ? t('ai.loadFailedHttp', { status: err.status }) : t('ai.loadFailed'));
      setState('error');
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  async function createKey(name: string): Promise<void> {
    try {
      const minted = await api<Minted>('/v1/portal/ai/api-keys', {
        method: 'POST',
        body: name ? { name } : {},
      });
      setModal({ kind: 'reveal', minted });
      setErrorDetail(null);
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrorDetail(
          err.code === 'key_cap_reached'
            ? t('ai.capReached', { cap: data?.api?.key_cap ?? DEFAULT_KEY_CAP })
            : t('ai.unavailableLater'),
        );
      } else {
        setErrorDetail(err instanceof ApiError ? t('ai.createFailedHttp', { status: err.status }) : t('ai.createFailed'));
      }
      setModal(null);
    }
  }

  async function revokeKey(keyId: string): Promise<void> {
    try {
      await api(`/v1/portal/ai/api-keys/${keyId}`, { method: 'DELETE' });
      setModal(null);
      void load();
    } catch (err) {
      setErrorDetail(err instanceof ApiError ? t('ai.revokeFailedHttp', { status: err.status }) : t('ai.revokeFailed'));
    }
  }

  const liveKeys = (data?.api_keys ?? []).filter((k) => k.status !== 'revoked');
  // OCP-770 - revoked keys whose last usage is still being charged.
  const settlingKeys = (data?.api_keys ?? []).filter(
    (k) => k.status === 'revoked' && k.final_accounting === 'pending',
  );
  const apiInfo = data?.api ?? null;
  const capReached = liveKeys.length >= (apiInfo?.key_cap ?? DEFAULT_KEY_CAP);

  return (
    <section className="mt-10" data-testid="ai-api-keys">
      <RoleReadOnlyNotice scope="infra" />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            {t('ai.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('ai.intro')}</p>
        </div>
        {data?.gateway_configured && canManageInfra && (
          <button
            type="button"
            data-testid="ai-key-create-btn"
            disabled={capReached}
            onClick={() => setModal({ kind: 'create' })}
            className="shrink-0 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('ai.create')}
          </button>
        )}
      </div>

      {errorDetail && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {errorDetail}
        </div>
      )}

      {state === 'loading' && <p className="mt-4 text-sm text-gray-500">{t('ai.loading')}</p>}

      {settlingKeys.length > 0 && (
        <div
          data-testid="ai-keys-final-accounting"
          className="mt-4 rounded-lg border border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-900/30 px-3 py-2 text-sm text-sky-900 dark:text-sky-200"
        >
          {t('ai.finalAccountingPending', {
            keys: settlingKeys.map((k) => `${k.key_prefix}…`).join(', '),
          })}
        </div>
      )}

      {data && !data.gateway_configured && (
        <div
          data-testid="ai-keys-unavailable"
          className="mt-4 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          {t('ai.unavailable')}
        </div>
      )}

      {data?.gateway_configured && (
        <>
          {liveKeys.length === 0 ? (
            <div
              data-testid="ai-keys-empty"
              className="mt-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500"
            >
              {t('ai.empty')}
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">{t('ai.colName')}</th>
                    <th className="px-3 py-2">{t('ai.colKey')}</th>
                    <th className="px-3 py-2">{t('ai.colStatus')}</th>
                    <th className="px-3 py-2">{t('ai.colLastUsed')}</th>
                    <th className="px-3 py-2 text-right">{t('ai.colActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {liveKeys.map((k) => (
                    <tr key={k.id} data-testid={`ai-key-row-${k.id}`}>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                        {k.name ?? '—'}
                        {k.created_via === 'operator' && (
                          <span className="ml-2 rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] uppercase text-gray-500">
                            {t('ai.provisioned')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <code className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs">
                          {k.key_prefix}…
                        </code>
                      </td>
                      <td className="px-3 py-2">
                        {k.gated ? (
                          <span className="rounded bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-800 dark:text-red-300">
                            {t('ai.paused')}
                          </span>
                        ) : (
                          <span className="rounded bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                            {t('ai.active')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {k.last_used_at ? formatDate(k.last_used_at) : t('ai.never')}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {canManageInfra && (
                        <button
                          type="button"
                          data-testid={`ai-key-revoke-${k.id}`}
                          onClick={() => setModal({ kind: 'confirm-revoke', key: k })}
                          className="text-xs text-red-700 dark:text-red-300 hover:underline"
                        >
                          {t('ai.revoke')}
                        </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {apiInfo && <UsageSnippet api={apiInfo} />}
        </>
      )}

      {modal?.kind === 'create' && (
        <CreateModal onCancel={() => setModal(null)} onCreate={(name) => void createKey(name)} />
      )}
      {modal?.kind === 'reveal' && (
        <RevealModal minted={modal.minted} onClose={() => setModal(null)} />
      )}
      {modal?.kind === 'confirm-revoke' && (
        <ConfirmRevokeModal
          keyToRevoke={modal.key}
          onCancel={() => setModal(null)}
          onConfirm={() => void revokeKey(modal.key.id)}
        />
      )}
    </section>
  );
}

function UsageSnippet({ api: info }: { api: ApiInfo }) {
  const { t } = useTranslation('api-keys');
  const url = `${info.base_url}${info.embeddings_path}`;
  const snippet = `curl ${url} \\
  -H "Authorization: Bearer $ORBOTO_AI_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${info.model}",
    "input": "your text here"
  }'`;
  return (
    <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('ai.snippetTitle')}</h3>
      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
        {t('ai.snippetBefore')} <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">{info.base_url}</code>
        {t('ai.snippetMiddle')} <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">{info.model}</code>
        {t('ai.snippetAfter')}
      </p>
      <pre
        data-testid="ai-key-snippet"
        className="mt-3 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100"
      >
        {snippet}
      </pre>
    </div>
  );
}

function CreateModal({ onCancel, onCreate }: { onCancel: () => void; onCreate: (name: string) => void }) {
  const { t } = useTranslation('api-keys');
  const [name, setName] = useState('');
  return (
    <ModalShell titleId="ai-key-create-title" title={t('ai.createTitle')} onCancel={onCancel}>
      <label className="block">
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{t('ai.nameLabel')}</span>
        <span className="block text-xs text-gray-600 dark:text-gray-400">
          {t('ai.nameHintBefore')} <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">prod ingest</code>.
        </span>
        <input
          type="text"
          autoFocus
          maxLength={64}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('ai.namePlaceholder')}
          data-testid="ai-key-name-input"
          className="mt-2 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
      </label>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('ai.cancel')}
        </button>
        <button
          type="button"
          data-testid="ai-key-create-submit"
          onClick={() => onCreate(name.trim())}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          {t('ai.createSubmit')}
        </button>
      </div>
    </ModalShell>
  );
}

function RevealModal({ minted, onClose }: { minted: Minted; onClose: () => void }) {
  const { t } = useTranslation('api-keys');
  const [copied, setCopied] = useTransientState(false, 2000);
  function copy() {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(minted.key);
      setCopied(true);
    }
  }
  return (
    <ModalShell titleId="ai-key-reveal-title" title={t('ai.createdTitle')} onCancel={onClose} hideCloseX>
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
        <strong>{t('ai.saveNow')}</strong>
        {t('ai.saveNowBody')}
      </div>
      <div className="mt-4">
        <span className="block text-xs text-gray-600 dark:text-gray-400">
          {t('ai.yourNew')} {minted.name ? <em>{minted.name}</em> : null} {t('ai.apiKey')}
        </span>
        <div className="mt-2 flex items-stretch gap-2">
          <code
            data-testid="ai-key-reveal-plaintext"
            className="min-w-0 flex-1 overflow-x-auto rounded bg-gray-900 px-3 py-2 text-xs text-gray-100"
          >
            {minted.key}
          </code>
          <button
            type="button"
            onClick={copy}
            className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            {copied ? t('ai.copied') : t('ai.copy')}
          </button>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          data-testid="ai-key-reveal-dismiss"
          onClick={onClose}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('ai.dismiss')}
        </button>
      </div>
    </ModalShell>
  );
}

function ConfirmRevokeModal({
  keyToRevoke,
  onCancel,
  onConfirm,
}: {
  keyToRevoke: KeySummary;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('api-keys');
  return (
    <ModalShell
      titleId="ai-key-revoke-title"
      title={t('ai.revokeTitle', { name: keyToRevoke.name ?? keyToRevoke.key_prefix })}
      onCancel={onCancel}
    >
      <p className="text-sm text-gray-600 dark:text-gray-400">{t('ai.revokeBody')}</p>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('ai.cancel')}
        </button>
        <button
          type="button"
          data-testid="ai-key-revoke-confirm"
          onClick={onConfirm}
          className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          {t('ai.revoke')}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  titleId,
  title,
  children,
  onCancel,
  hideCloseX,
}: {
  titleId: string;
  title: string;
  children: React.ReactNode;
  onCancel: () => void;
  hideCloseX?: boolean;
}) {
  const { t } = useTranslation('api-keys');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-gray-900 p-5 shadow-xl">
        <header className="mb-4 flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-base font-semibold">
            {title}
          </h2>
          {!hideCloseX && (
            <button
              type="button"
              onClick={onCancel}
              aria-label={t('ai.close')}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60"
            >
              ✕
            </button>
          )}
        </header>
        {children}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  return fmtDate(iso, iso);
}
